import React, { ChangeEvent, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Backdrop,
    CircularProgress,
    TextField,
    Button,
    Typography,
    FormControl,
    Grid2,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    SelectChangeEvent,
    MenuItem,
    InputLabel,
    Select,
    IconButton,
} from '@mui/material';
import { RootState } from '../../redux/reducers';
import { importAllGoods } from '../../redux/actions/goodsActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import GoodsTable from './GoodsTable';
import { GoodsStatus, SiteType } from '../../api/ClientApi';
import { GoodsStatusSR, SiteTypeSR } from '../../common/Types';
import { TableDetailsType, TableDetailsTypeSR } from './GoodsHelper';
import ChooseSite from './GoodsToSiteForm/ChooseSite';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { importApprovedOrderGoodsItems } from '../../redux/actions/orderGoodsItemsActions';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import {
    canManipulateGoods,
    isEmployeeFinanceManager,
    isEmployeeProjectManager,
    isEmployeeWarehouseManager,
} from '../../redux/selectors/employeeSelector';
import { useLocation, useParams } from 'react-router-dom';

import RefreshIcon from '@mui/icons-material/Refresh';

export default function GoodsList(): ReactElement {
    const goods = useAppSelector((state: RootState) => state.goods.goods);
    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const canAddGoods = useAppSelector(canManipulateGoods);

    const [articleFilter, setArticleFilter] = React.useState<string>('');
    const [labelsFilter, setLabelsFilter] = React.useState<string>('');
    const [uniqueBatchIdFilter, setUniqueBatchIdFilter] = React.useState<string>('');
    const [batchSuppliersFilter, setBatchSupplierFilter] = React.useState<string>('');
    const [siteNameFilter, setSiteNameFilter] = React.useState<string>('');
    const [siteTypeFilter, setSiteTypeFilter] = React.useState<SiteType | 'Svi'>('Svi');
    const [goodsStatusFilter, setGoodsStatusFilter] = React.useState<GoodsStatus>(GoodsStatus.InStock);

    const [tableDetailsType, setTableDetailsType] = useState<TableDetailsType | null>(TableDetailsType.Basic);

    const dispatch = useAppDispatch();

    const location = useLocation();
    const path = location.pathname;

    const params = useParams();

    const isProject = path.indexOf('/projects') !== -1;

    useEffect(() => {
        if (!goods) {
            dispatch(importAllGoods());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch]);

    const currGoods = useMemo(() => {
        if (isProject) {
            const projId = params.projectId;

            if (projId !== undefined && !isNaN(+projId) && +projId > 0) {
                return goods?.filter((g) => g.site?.projectId === +projId);
            }
        }
        return goods?.filter((g) => !!g.siteId);
    }, [goods, path]);

    const [openChooseSite, setOpenChooseSite] = useState<boolean>(false);

    const handleOpenChooseSite = () => {
        setOpenChooseSite(true);
    };

    const handleCloseChooseSite = () => {
        setOpenChooseSite(false);
    };

    const handleArticleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setArticleFilter(value);
    };

    const handleLabelFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLabelsFilter(value);
    };

    const handleUniqueBatchIdFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setUniqueBatchIdFilter(value);
    };

    const handleBatchSupplierFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setBatchSupplierFilter(value);
    };

    const handleSiteNameFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSiteNameFilter(value);
    };

    const handleSiteTypeFilterChange = (event: SelectChangeEvent<SiteType | 'Svi'>) => {
        const value = event.target.value;

        switch (value) {
            case SiteType[SiteType.Warehouse]:
                setSiteTypeFilter(SiteType.Warehouse);
                break;
            case SiteType[SiteType.ConstructionSite]:
                setSiteTypeFilter(SiteType.ConstructionSite);
                break;
            case SiteType[SiteType.Depot]:
                setSiteTypeFilter(SiteType.Depot);
                break;
            default:
                setSiteTypeFilter('Svi');
        }
    };

    const handleGoodsStatusFilterChange = (event: SelectChangeEvent<GoodsStatus>) => {
        const value = event.target.value;

        switch (value) {
            case GoodsStatus[GoodsStatus.Sold]:
                setGoodsStatusFilter(GoodsStatus.Sold);
                break;
            case GoodsStatus[GoodsStatus.Used]:
                setGoodsStatusFilter(GoodsStatus.Used);
                break;
            case GoodsStatus[GoodsStatus.WrittenOff]:
                setGoodsStatusFilter(GoodsStatus.WrittenOff);
                break;
            default:
                setGoodsStatusFilter(GoodsStatus.InStock);
        }
    };

    const clearAllFilters = () => {
        setArticleFilter('');
        setLabelsFilter('');
        setSiteNameFilter('');
        setSiteTypeFilter('Svi');
        setUniqueBatchIdFilter('');
        setBatchSupplierFilter('');
        setGoodsStatusFilter(GoodsStatus.InStock);
    };

    const handleChangeTableDetailsType = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: any) => {
        setTableDetailsType(value);
    };

    const isBasicView = tableDetailsType === TableDetailsType.Basic || !tableDetailsType;
    const isExitIfoView = tableDetailsType === TableDetailsType.Exit;

    return goods === null ? (
        <Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(goods)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                <Grid2 sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5">Roba</Typography>
                    <Tooltip title="Osveži">
                        <IconButton
                            color="primary"
                            aria-label="Osveži"
                            onClick={() => {
                                dispatch(importAllGoods());
                                dispatch(importApprovedOrderGoodsItems());
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Grid2>
                {canAddGoods && !isProject && (
                    <Grid2>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleOpenChooseSite}
                            startIcon={<AddIcon />}
                            sx={{ textTransform: 'none' }}
                        >
                            Ubaci robu
                        </Button>
                    </Grid2>
                )}
            </Grid2>
            <hr />
            <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                <Grid2 container sx={{ display: 'flex', gap: 2, flexDirection: 'start' }}>
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: '200px' }}
                        placeholder="Artikal"
                        value={articleFilter}
                        onChange={handleArticleFilterChange}
                    />
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: '200px' }}
                        placeholder="Tag"
                        value={labelsFilter}
                        onChange={handleLabelFilterChange}
                    />
                    {!isBasicView && !isExitIfoView && (
                        <TextField
                            size="small"
                            sx={{ mb: 1, width: '200px' }}
                            placeholder="Br.fakture"
                            value={uniqueBatchIdFilter}
                            onChange={handleUniqueBatchIdFilterChange}
                        />
                    )}
                    {!isBasicView && !isExitIfoView && (
                        <TextField
                            size="small"
                            sx={{ mb: 1, width: '200px' }}
                            placeholder="Dobavljač"
                            value={batchSuppliersFilter}
                            onChange={handleBatchSupplierFilterChange}
                        />
                    )}
                    {!isBasicView && (
                        <TextField
                            size="small"
                            sx={{ mb: 1, width: '200px' }}
                            placeholder="Skladište"
                            value={siteNameFilter}
                            onChange={handleSiteNameFilterChange}
                        />
                    )}
                    {!isBasicView && (
                        <FormControl size="small" sx={{ mb: 1, width: '200px' }}>
                            <InputLabel id="demo-simple-select-label">Tip skladišta</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={siteTypeFilter ?? ''}
                                label="Tip skladišta"
                                onChange={handleSiteTypeFilterChange}
                            >
                                {Object.values(SiteType).map((st) => {
                                    return (
                                        <MenuItem key={st} value={st as SiteType}>
                                            {SiteTypeSR[st]}
                                        </MenuItem>
                                    );
                                })}
                                <MenuItem key={'Svi'} value={'Svi'}>
                                    {'Svi'}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    {!isBasicView && (
                        <FormControl size="small" sx={{ mb: 1, width: '200px' }}>
                            <InputLabel id="demo-simple-select-label">Status robe</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={goodsStatusFilter ?? ''}
                                label="Status robe"
                                onChange={handleGoodsStatusFilterChange}
                            >
                                {Object.values(GoodsStatus).map((gs) => {
                                    if (gs !== GoodsStatus.InTransport && gs !== GoodsStatus.Ordered) {
                                        return (
                                            <MenuItem key={gs} value={gs as GoodsStatus}>
                                                {GoodsStatusSR[gs]}
                                            </MenuItem>
                                        );
                                    }
                                })}
                            </Select>
                        </FormControl>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Box>
                        <Button onClick={clearAllFilters}>Očisti filtere</Button>
                    </Box>
                </Grid2>
                <Box sx={{ p: 0, mt: 2 }} />
                <Box display="flex" justifyContent="end">
                    <ToggleButtonGroup
                        color="primary"
                        value={tableDetailsType}
                        exclusive
                        onChange={handleChangeTableDetailsType}
                    >
                        <Tooltip title={TableDetailsTypeSR[TableDetailsType.Basic]}>
                            <ToggleButton value={TableDetailsType.Basic}>
                                <DensityLargeIcon />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title={TableDetailsTypeSR[TableDetailsType.Exit]}>
                            <ToggleButton value={TableDetailsType.Exit}>
                                <DensityMediumIcon />
                            </ToggleButton>
                        </Tooltip>
                        {(isFinanceManager || isProjectManager || isWarehouseManager) && (
                            <Tooltip title={TableDetailsTypeSR[TableDetailsType.Arrival]}>
                                <ToggleButton value={TableDetailsType.Arrival}>
                                    <DensitySmallIcon />
                                </ToggleButton>
                            </Tooltip>
                        )}
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ p: 0, mt: 2 }} />
                <DetailsTableContainerBox>
                    <GoodsTable
                        isSiteTable={false}
                        isSiteWithClient={false}
                        tableDetailsType={tableDetailsType}
                        goods={currGoods ?? null}
                        approvedOrderGoodsItems={approvedOrderGoodsItems}
                        articleFilter={articleFilter.trim()}
                        labelsFilter={labelsFilter.trim()}
                        goodsStatusFilter={goodsStatusFilter}
                        batchSuppliersFilter={batchSuppliersFilter.trim()}
                        siteNameFilter={siteNameFilter.trim()}
                        siteTypeFilter={siteTypeFilter === 'Svi' ? null : siteTypeFilter}
                        uniqueBatchIdFilter={uniqueBatchIdFilter.trim()}
                    />
                </DetailsTableContainerBox>
            </Box>
            <ChooseSite open={openChooseSite} closeForm={handleCloseChooseSite} />
        </DetailsContainerBox>
    );
}
